import React, { useState, useEffect } from 'react';
import { Copy, Check } from 'lucide-react';
import { systemService } from '../../../services';

const DefaultsTab: React.FC = () => {
    const [defaults, setDefaults] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [viewMode, setViewMode] = useState<'pretty' | 'raw'>('pretty');
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        fetchDefaults();
    }, []);

    const fetchDefaults = async () => {
        try {
            setLoading(true);
            const data = await systemService.getDefaults();
            setDefaults(data);
        } catch (err: any) {
            setError(err.message || 'Failed to load default configuration');
        } finally {
            setLoading(false);
        }
    };

    const handleCopy = () => {
        if (defaults) {
            navigator.clipboard.writeText(JSON.stringify(defaults, null, 2));
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    if (loading) {
        return <div className="text-center py-8">Loading default configuration...</div>;
    }

    if (error) {
        return (
            <div className="bg-red-50 text-red-700 p-4 rounded-lg flex items-center">
                <span className="mr-2">⚠️</span>
                {error}
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-lg font-medium text-gray-900">Default Configuration</h2>
                    <p className="text-sm text-gray-500 mt-1">
                        View the system default configuration for Applications, Groups, and Routing Policies.
                        These defaults are used when populating new tenants.
                    </p>
                </div>
                <div className="flex space-x-2">
                    <div className="flex bg-gray-100 p-1 rounded-lg">
                        <button
                            onClick={() => setViewMode('pretty')}
                            className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${viewMode === 'pretty'
                                ? 'bg-white shadow text-primary-600'
                                : 'text-gray-500 hover:text-gray-900'
                                }`}
                        >
                            Pretty
                        </button>
                        <button
                            onClick={() => setViewMode('raw')}
                            className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${viewMode === 'raw'
                                ? 'bg-white shadow text-primary-600'
                                : 'text-gray-500 hover:text-gray-900'
                                }`}
                        >
                            Raw JSON
                        </button>
                    </div>
                    <button
                        onClick={handleCopy}
                        className="btn-secondary inline-flex items-center space-x-2"
                        title="Copy to clipboard"
                    >
                        {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                        <span>{copied ? 'Copied' : 'Copy'}</span>
                    </button>
                </div>
            </div>

            <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                <div className="p-4 overflow-auto max-h-[600px] font-mono text-sm">
                    {viewMode === 'pretty' ? (
                        <div className="space-y-6">
                            {defaults.applications && (
                                <div>
                                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center">
                                        <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                                        Applications ({defaults.applications.length})
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {defaults.applications.map((app: any, idx: number) => (
                                            <div key={idx} className="bg-white p-3 rounded border border-gray-200 shadow-sm">
                                                <div className="font-medium text-gray-900">{app.name}</div>
                                                <div className="text-gray-500 text-xs mt-1">{app.description}</div>
                                                <div className="mt-2 flex flex-wrap gap-1">
                                                    {app.category && (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                                            {app.category}
                                                        </span>
                                                    )}
                                                    {app.protocol && (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                                                            {app.protocol}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {defaults.applicationGroups && (
                                <div>
                                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center">
                                        <span className="w-2 h-2 bg-indigo-500 rounded-full mr-2"></span>
                                        Application Groups ({defaults.applicationGroups.length})
                                    </h3>
                                    <div className="space-y-3">
                                        {defaults.applicationGroups.map((group: any, idx: number) => (
                                            <div key={idx} className="bg-white p-3 rounded border border-gray-200 shadow-sm">
                                                <div className="font-medium text-gray-900 flex justify-between">
                                                    {group.name}
                                                    <span className="text-xs text-gray-500 font-normal">{group.members.length} members</span>
                                                </div>
                                                <div className="text-gray-500 text-xs mt-1 mb-2">{group.description}</div>
                                                <div className="text-xs text-gray-400">
                                                    Members: {group.members.join(', ')}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {defaults.routingPolicies && (
                                <div>
                                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center">
                                        <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                                        Routing Policies ({defaults.routingPolicies.length})
                                    </h3>
                                    <div className="space-y-3">
                                        {defaults.routingPolicies.map((policy: any, idx: number) => (
                                            <div key={idx} className="bg-white p-3 rounded border border-gray-200 shadow-sm">
                                                <div className="font-medium text-gray-900 border-b border-gray-100 pb-2 mb-2">
                                                    {policy.name}
                                                    <span className="ml-2 text-xs text-gray-500 font-normal">Priority: {policy.priority}</span>
                                                </div>
                                                <div className="space-y-2">
                                                    {policy.rules.map((rule: any, rIdx: number) => (
                                                        <div key={rIdx} className="text-xs flex items-center justify-between bg-gray-50 p-2 rounded">
                                                            <span className="font-medium text-gray-700">{rule.name}</span>
                                                            <span className="text-gray-500">
                                                                {rule.matchType === 'any' ? 'Any Traffic' : `${rule.matchType}: ${rule.matchValue}`}
                                                                {' → '}
                                                                <span className={rule.action_type === 'permit' ? 'text-green-600' : 'text-red-600'}>
                                                                    {rule.action_type}
                                                                </span>
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <pre className="text-gray-800 whitespace-pre-wrap">
                            {JSON.stringify(defaults, null, 2)}
                        </pre>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DefaultsTab;
